import { useMemo, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { useTranslation } from "react-i18next"

import useLayoutStore from "../../store/useLayoutStore"
import useAppStore from "../../store/useAppStore"
import useAuthStore from "../../store/useAuthStore"
import SearchBar from "../elements/SearchBar"
import AdminStatsView from "./stats/AdminStatsView"
import UserStatsView from "./stats/UserStatsView"

const ALL_GROUPS_VALUE = "Sve grupe"
const NO_GROUP_LABEL = "Bez grupe"

const displayGroupName = (name) => {
    if (name == null || name === "" || name === "null" || name === "undefined") return NO_GROUP_LABEL
    return name
}

const StatsView = () => {
    const { t } = useTranslation("stats")
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const userRole = useAuthStore(state => state.userRole)
    const [employeeSearch, setEmployeeSearch] = useState("")

    const activeThemeClass = isDarkTheme ? 'dark-color' : 'white-color'
    const isAdmin = userRole === "admin"

    return (
        <div className="view-container">
            <div className="view-header-container">
                <div className="view-header-top">
                    <div className={`v-header-name ${activeThemeClass}`}>{t("title")}</div>
                    <div className={`v-header-description ${isDarkTheme ? "dark-color" : ""}`}>{t("description")}</div>
                </div>
            </div>
            <div className="cv-content-container">
                <div className="cv-content-div">
                    <StatsToolbar isAdmin={isAdmin} onSearchChange={setEmployeeSearch} />
                    {isAdmin ? <AdminStatsView employeeSearch={employeeSearch} /> : <UserStatsView />}
                </div>
            </div>
        </div>
    )
}

const StatsToolbar = ({ isAdmin, onSearchChange }) => {
    const { t } = useTranslation("stats")
    const statsFilter = useAppStore(state => state.statsFilter)
    const statsDateFilter = useAppStore(state => state.statsDateFilter)
    const userStatsDateFilter = useAppStore(state => state.userStatsDateFilter)
    const setStatsDateFilterMonth = useAppStore(state => state.setStatsDateFilterMonth)
    const setStatsDateFilterYear = useAppStore(state => state.setStatsDateFilterYear)
    const setUserStatsDateFilterMonth = useAppStore(state => state.setUserStatsDateFilterMonth)
    const setUserStatsDateFilterYear = useAppStore(state => state.setUserStatsDateFilterYear)
    const ensureStatsMonthData = useAppStore(state => state.ensureStatsMonthData)
    const ensureUserStatsMonthData = useAppStore(state => state.ensureUserStatsMonthData)
    const setStatsFilterGroup = useAppStore(state => state.setStatsFilterGroup)
    const groupsState = useAppStore(state => state.groupsState)
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const smallScreen = useLayoutStore(state => state.smallScreen)

    const dateFilter = isAdmin ? statsDateFilter : userStatsDateFilter
    const months = t("months", { returnObjects: true })

    const [isMonthOpen, setIsMonthOpen] = useState(false)
    const [isYearOpen, setIsYearOpen] = useState(false)
    const [isGroupOpen, setIsGroupOpen] = useState(false)

    const dropdownOptions = useMemo(() => {
        const rawKeys = Object.keys(groupsState || {})
        const displayNames = [...new Set(rawKeys.map(displayGroupName))].sort()
        return [ALL_GROUPS_VALUE, ...displayNames]
    }, [groupsState])

    const handleMonthChange = async (month) => {
        if (isAdmin) {
            setStatsDateFilterMonth(month)
            ensureStatsMonthData(month, statsDateFilter.year)
        } else {
            setUserStatsDateFilterMonth(month)
            ensureUserStatsMonthData(month, userStatsDateFilter.year)
        }
        setIsMonthOpen(false)
    }

    const handleYearChange = async (year) => {
        if (isAdmin) {
            setStatsDateFilterYear(year)
            ensureStatsMonthData(statsDateFilter.month, year)
        } else {
            setUserStatsDateFilterYear(year)
            ensureUserStatsMonthData(userStatsDateFilter.month, year)
        }
        setIsYearOpen(false)
    }

    return (
        <div className="cv-toolbar-container">
            <div className="cv-filters">
                <div className="cv-filter-item">
                    <label className={`${isDarkTheme ? "dark-color" : ""}`}>{t("filters.month")}</label>
                    <div
                        className={`cv-dropdown ${isDarkTheme ? "dark-dropdown" : ""}`}
                        onClick={() => setIsMonthOpen(!isMonthOpen)}
                    >
                        {months[dateFilter.month]}
                        <motion.div className="arrow">
                            <svg viewBox="0 0 24 24" width="24" height="24" style={{ display: "block" }}>
                                <path d="M7 10l5 5 5-5" fill="none" stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </motion.div>
                        <AnimatePresence>
                            {isMonthOpen && (
                                <motion.div className={`cv-dropdown-list ${isDarkTheme ? "dark-dropdownlist" : ""}`} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.14, ease: "easeOut" }}>
                                    {months.map((monthName, idx) => (
                                        <div key={monthName} className={`cv-dropdown-item ${isDarkTheme ? "dark-dropdownitem" : ""}`} onClick={(e) => { e.stopPropagation(); handleMonthChange(idx) }}>
                                            {monthName}
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
                <div className="cv-filter-item">
                    <label className={`${isDarkTheme ? "dark-color" : ""}`}>{t("filters.year")}</label>
                    <div
                        className={`cv-dropdown ${isDarkTheme ? "dark-dropdown" : ""}`}
                        onClick={() => setIsYearOpen(!isYearOpen)}
                    >
                        {dateFilter.year}
                        <motion.div className="arrow">
                            <svg viewBox="0 0 24 24" width="24" height="24" style={{ display: "block" }}>
                                <path d="M7 10l5 5 5-5" fill="none" stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </motion.div>
                        <AnimatePresence>
                            {isYearOpen && (
                                <motion.div className={`cv-dropdown-list ${isDarkTheme ? "dark-dropdownlist" : ""}`} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.14, ease: "easeOut" }}>
                                    {Array.from({ length: 6 }, (_, i) => {
                                        const currentYear = new Date().getFullYear()
                                        const year = i === 0 ? currentYear + 1 : currentYear - (i - 1)
                                        return (
                                            <div key={year} className={`cv-dropdown-item ${isDarkTheme ? "dark-dropdownitem" : ""}`} onClick={(e) => { e.stopPropagation(); handleYearChange(year) }}>
                                                {year}
                                            </div>
                                        )
                                    })}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
            {isAdmin && (
                <div className="cv-tools-container cv-tools-container--search-end">
                    <div className="cv-tools-div">
                        {/* <div className="cv-filter-item">
                            <label className={`cv-groups-filter-label ${isDarkTheme ? "dark-color" : ""}`}>Filter po grupama</label>
                            <div className={`cv-dropdown cv-groups-dropdown ${isDarkTheme ? "dark-dropdown" : ""}`} onClick={() => setIsGroupOpen((prev) => !prev)}>
                                <span className="cv-dropdown-value">{statsFilter.group}</span>
                                <motion.div className="arrow">
                                    <svg viewBox="0 0 24 24" width="24" height="24" style={{ display: "block" }}>
                                        <path d="M7 10l5 5 5-5" fill="none" stroke={isDarkTheme ? "rgb(219, 219, 219)" : "black"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </motion.div>
                                <AnimatePresence>
                                    {isGroupOpen && (
                                        <motion.div className={`cv-dropdown-list ${isDarkTheme ? "dark-dropdownlist" : ""}`} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.14, ease: "easeOut" }}>
                                            {dropdownOptions.map((name) => (
                                                <div key={name} className={`cv-dropdown-item ${isDarkTheme ? "dark-dropdownitem" : ""} ${statsFilter.group === name ? "cv-dropdown-item-selected" : ""}`} onClick={(e) => { e.stopPropagation(); setStatsFilterGroup(name); setIsGroupOpen(false) }}>
                                                    {name}
                                                </div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div> */}
                        <SearchBar
                            onSearchChange={onSearchChange}
                            expandedWidth={smallScreen ? 224 : 244}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default StatsView
